import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { langObject } from 'vendor/application';
import { showNotify, updateProjectsStatus } from 'vendor/application/disptach';
import { ProjectAPI } from 'domains/Project/infrastructure';
import {
  Button,
  Card,
  CardActions,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';

const LANGUAGES = Array.isArray(langObject()) ? langObject() : Object.values(langObject() || {});

const INITIAL_PROJECT_STATE = {
  dialog_flow_id: '',
  lang: 'es',
  credentials_content: '',
  centribot_project_id: '',
};

const useForm = (initialState, validateFn) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const buildPayload = useCallback(() => {
    const payload = { ...values };
    const trimmed = values.centribot_project_id?.trim();
    payload.centribot_project_id = trimmed || null;
    return payload;
  }, [values]);

  const handleChange = useCallback((e, field) => {
    updateField(field, e.target.value);
  }, [updateField]);

  const validate = useCallback(() => {
    const validationErrors = validateFn(values);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [values, validateFn]);

  const reset = useCallback(() => {
    setValues(initialState);
    setErrors({});
    setIsSubmitting(false);
  }, [initialState]);

  return {
    values,
    errors,
    isSubmitting,
    setIsSubmitting,
    updateField,
    handleChange,
    buildPayload,
    validate,
    reset,
  };
};

const validateProject = (project) => {
  const errors = {};
  if (!project.dialog_flow_id.trim()) {
    errors.dialog_flow_id = 'Dialog Flow ID is required';
  }
  if (project.credentials_content.trim()) {
    try {
      JSON.parse(project.credentials_content);
    } catch (error) {
      console.error('Failed to parse credentials_content as JSON:', error);
      errors.credentials_content = 'Invalid JSON format';
    }
  } else {
    errors.credentials_content = 'Credentials are required';
  }
  return errors;
};

const ProjectCreate = ({ access, dispatch, getAllProjects, history }) => {
  const form = useForm(INITIAL_PROJECT_STATE, validateProject);

  const handleCreateProject = useCallback(async (e) => {
    e.preventDefault();
    if (!form.validate()) {
      dispatch(showNotify({
        message: 'Please fill in all required fields correctly',
        severity: 'warning',
      }));
      return;
    }

    form.setIsSubmitting(true);
    try {
      const payload = form.buildPayload();
      const api = new ProjectAPI(null, access, { dispatch }, payload);
      await api.post_project();
      dispatch(updateProjectsStatus(true));
      if (getAllProjects) {
        await getAllProjects();
      }
      dispatch(showNotify({
        message: 'The project was created successfully.',
        severity: 'success',
      }));
      history.push('/projects');
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'An unexpected error occurred';

      dispatch(showNotify({
        message: errorMessage,
        severity: 'error',
      }));
    } finally {
      form.setIsSubmitting(false);
    }
  }, [form, access, dispatch, getAllProjects, history]);

  const handleCancel = useCallback(() => {
    history.push('/projects');
  }, [history]);

  return (
    <div className="cen-project-create-container">
      <Card elevation={3} className="cen-action-card">
        <div className="cen-border" />
        <div className="cen-action-header">
          <h1>Create Project</h1>
          <CardActions>
            <Button
              className="cen-btn-cancel"
              variant="outlined"
              onClick={handleCancel}
              disabled={form.isSubmitting}
              aria-label="Cancel project creation"
            >
              CANCEL
            </Button>

            <Button
              className="cen-btn-create"
              variant="outlined"
              onClick={handleCreateProject}
              disabled={form.isSubmitting}
              aria-label="Create project"
            >
              {form.isSubmitting ? <CircularProgress size={21} /> : 'CREATE'}
            </Button>
          </CardActions>
        </div>

        <form className="cen-action-body" onSubmit={handleCreateProject}>
          <div className="cen-action-element">
            <div>
              <span>Dialog Flow ID *</span>
              <span>Agent ID from Dialogflow project.</span>
            </div>
            <TextField
              value={form.values.dialog_flow_id}
              onChange={(e) => form.handleChange(e, 'dialog_flow_id')}
              disabled={form.isSubmitting}
              error={Boolean(form.errors.dialog_flow_id)}
              helperText={form.errors.dialog_flow_id}
              required
              fullWidth
              aria-label="Dialog Flow ID"
              inputProps={{ 'aria-required': 'true' }}
            />
          </div>

          <Divider />

          <div className="cen-action-element">
            <div>
              <span>Centribot Project ID</span>
              <span>Project ID from Centribot account.</span>
            </div>
            <TextField
              value={form.values.centribot_project_id}
              onChange={(e) => form.handleChange(e, 'centribot_project_id')}
              disabled={form.isSubmitting}
              error={Boolean(form.errors.centribot_project_id)}
              helperText={form.errors.centribot_project_id}
              fullWidth
              aria-label="Centribot Project ID"
            />
          </div>

          <Divider />

          <div className="cen-action-element">
            <div>
              <span>Language *</span>
              <span>Language set in Dialogflow Agent.</span>
            </div>
            <FormControl fullWidth disabled={form.isSubmitting}>
              <Select
                value={form.values.lang}
                onChange={(e) => form.handleChange(e, 'lang')}
                aria-label="Select language"
                inputProps={{ 'aria-required': 'true' }}
              >
                {LANGUAGES.map((language) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Required</FormHelperText>
            </FormControl>
          </div>

          <Divider />

          <div className="cen-action-element-grid">
            <div>
              <span>Credentials *</span>
              <span>Dialogflow credential JSON file content for authentication.</span>
            </div>
            <TextField
              multiline
              minRows={10}
              value={form.values.credentials_content}
              variant="outlined"
              placeholder="Copy and paste Dialogflow credential file content (JSON format)"
              onChange={(e) => form.handleChange(e, 'credentials_content')}
              disabled={form.isSubmitting}
              error={Boolean(form.errors.credentials_content)}
              helperText={form.errors.credentials_content}
              required
              fullWidth
              aria-label="Credentials JSON content"
              inputProps={{ 'aria-required': 'true' }}
            />
          </div>
        </form>
      </Card>
    </div>
  );
};

ProjectCreate.propTypes = {
  access: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  getAllProjects: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

ProjectCreate.defaultProps = {
  getAllProjects: null,
};

const mapStateToProps = (state) => ({
  access: state.access,
});

export default withRouter(connect(mapStateToProps)(ProjectCreate));